import { gql } from "@apollo/client";

// ==========================================
// EMPLOYEE QUERIES
// ==========================================

export const GET_EMPLOYEES_PAGINATED = gql`
    query GetEmployeesPaginated($input: EmployeePaginationInput) {
        employeesPaginated(input: $input) {
            employees {
                id
                employeeNumber
                preferredName
                client {
                    id
                    fName
                    lName
                    email
                    phoneNumber
                    username
                    hasPasskey
                    lastPasskeyUsedAt
                }
                mobileNumber
                workEmail
                workPhone
                roleIds
                roles {
                    id
                    name
                    color
                }
                departmentIds
                departments {
                    id
                    name
                    color
                }
                locationIds
                locations {
                    id
                    name
                }
                positionIds
                positions {
                    id
                    name
                    color
                }
                position
                status
                startDate
                endDate
                contractType
                employmentType
                isShiftWorker
                hoursBasis
                minHoursPerWeek
                maxHoursPerWeek
                hourlyRate
                annualSalary
                awardClassificationId
                awardClassification {
                    id
                    code
                    name
                    awardName
                    baseHourlyRate
                    casualHourlyRate
                }
                bankAccountName
                bankBSB
                bankAccountNumber
                taxFileNumber
                superannuationFund
                dateOfBirth
                address {
                    street
                    street2
                    city
                    state
                    postcode
                    country
                }
                emergencyContact {
                    name
                    relationship
                    phoneNumber
                    email
                }
                company {
                    id
                    name
                }
            }
            total
            hasMore
            page
            totalPages
        }
        employeeStats
    }
`;

export const GET_EMPLOYEE = gql`
    query GetEmployee($id: ID!) {
        employee(id: $id) {
            id
            clientId
            employeeNumber
            preferredName
            isDigitalManager
            digitalManagerId
            client {
                id
                fName
                lName
                email
                phoneNumber
                username
                permissions
                lastLoginAt
                lastLoginMethod
                hasPasskey
                lastPasskeyUsedAt
            }
            mobileNumber
            workEmail
            workPhone
            roleIds
            roles {
                id
                name
                color
            }
            departmentIds
            departments {
                id
                name
                color
            }
            locationIds
            locations {
                id
                name
            }
            positionIds
            positions {
                id
                name
                color
            }
            position
            status
            startDate
            endDate
            employmentType
            hourlyRate
            payRates {
                rate
                effectiveFrom
                effectiveTo
            }
            annualSalary
            awardClassificationId
            awardClassification {
                id
                code
                name
                awardName
                baseHourlyRate
                casualHourlyRate
            }
            customAwardName
            hoursBasis
            minHoursPerWeek
            maxHoursPerWeek
            address {
                street
                city
                state
                postcode
                country
            }
            dateOfBirth
            taxFileNumber
            superannuationFund
            taxResidencyStatus
            claimsTaxFreeThreshold
            hasStudyTrainingLoan
            superChoiceType
            superFundABN
            superFundUSI
            superMemberNumber
            superNameOnAccount
            smsfAbn
            smsfEsa
            smsfBankAccountName
            smsfBankBSB
            smsfBankAccountNumber
            bankAccountName
            bankAccountNumber
            bankBSB
            emergencyContact {
                name
                relationship
                phoneNumber
                email
            }
            qualifications
            certifications
            notes
            contractType
            isShiftWorker
            workingRights
            profilePhoto
            createdAt
            updatedAt
        }
    }
`;

export const GET_EMPLOYEE_EDIT = gql`
    query GetEmployee($id: ID!) {
        employee(id: $id) {
            id
            clientId
            companyId
            employeeNumber
            preferredName
            client {
                id
                fName
                lName
                email
                phoneNumber
            }
            mobileNumber
            workEmail
            workPhone
            dateOfBirth
            address {
                street
                city
                state
                postcode
                country
            }
            roleIds
            roles {
                id
                name
                color
            }
            departmentIds
            departments {
                id
                name
                color
            }
            locationIds
            locations {
                id
                name
            }
            positionIds
            positions {
                id
                name
                color
            }
            position
            status
            startDate
            endDate
            contractType
            isShiftWorker
            workingRights
            hoursBasis
            minHoursPerWeek
            maxHoursPerWeek
            hourlyRate
            payRates {
                rate
                effectiveFrom
                effectiveTo
            }
            annualSalary
            awardClassificationId
            awardClassification {
                id
                code
                name
                awardName
                baseHourlyRate
                casualHourlyRate
            }
            customAwardName
            taxFileNumber
            superannuationFund
            bankAccountName
            bankAccountNumber
            bankBSB
            emergencyContact {
                name
                relationship
                phoneNumber
                email
            }
            qualifications
            certifications
            notes
        }
    }
`;

export const GET_EMPLOYEE_SHIFTS = gql`
    query GetEmployeeShifts($employeeId: ID!) {
        shiftsByEmployee(employeeId: $employeeId) {
            id
            title
            shiftType
            status
            startTime
            endTime
            locationNames
            departmentId
            jobIds
        }
    }
`;

/**
 * Termination guardrail (2026-07-28): every shift this employee is assigned to, with
 * enough on each assignment to decide (a) MASTER template vs LIVE/DRAFT, (b) whether it's
 * in the future and not yet terminal, and (c) whether it already carries recorded leave —
 * `removeEmployeeFromShift` deletes the whole assignment subdocument, absences and all, so
 * a shift with recorded leave must never be silently auto-removed. Companion-module query:
 * a tenant without the shifts module has no `shiftsByEmployee` field, so this is always
 * called with `errorPolicy: "ignore"` and an empty/undefined result just means "nothing to
 * guard, save normally" rather than a hard error.
 */
export const GET_EMPLOYEE_TERMINATION_IMPACT = gql`
    query GetEmployeeTerminationImpact($employeeId: ID!) {
        shiftsByEmployee(employeeId: $employeeId) {
            id
            title
            rosterMode
            status
            startTime
            endTime
            departmentId
            assignments {
                employeeId
                status
                absenceReason
                absences {
                    reason
                }
            }
        }
    }
`;

/**
 * Module-local copy of the shifts module's removal mutation — the employees module
 * cannot import shifts-module query docs (import contract), so this is a plain GraphQL
 * document string calling the same backend mutation, kept in sync by wording.
 */
export const REMOVE_EMPLOYEE_FROM_SHIFT = gql`
    mutation RemoveEmployeeFromShiftForTermination(
        $shiftId: ID!
        $employeeId: ID!
        $reason: String
        $notes: String
        $announce: Boolean
    ) {
        removeEmployeeFromShift(
            shiftId: $shiftId
            employeeId: $employeeId
            reason: $reason
            notes: $notes
            announce: $announce
        ) {
            id
        }
    }
`;

// ==========================================
// EMPLOYEE MUTATIONS
// ==========================================

export const CREATE_EMPLOYEE = gql`
    mutation CreateEmployee($input: EmployeeInput!) {
        createEmployee(input: $input) {
            id
            employeeNumber
            client {
                id
                fName
                lName
                email
            }
            status
        }
    }
`;

export const UPDATE_EMPLOYEE = gql`
    mutation UpdateEmployee($id: ID!, $input: UpdateEmployeeInput!) {
        updateEmployee(id: $id, input: $input) {
            id
            employeeNumber
            client {
                id
                fName
                lName
                email
            }
            status
            hoursBasis
            minHoursPerWeek
            maxHoursPerWeek
        }
    }
`;

// HR-scoped: updates the Employee record AND the linked Client's personal
// email / phone in one call. Use when editing from the employee detail card
// so HR doesn't need Clients-module access to fix a personal email/phone.
export const UPDATE_EMPLOYEE_WITH_PERSONAL = gql`
    mutation UpdateEmployeeWithPersonal(
      $id: ID!,
      $input: UpdateEmployeeInput!,
      $clientPersonal: UpdateEmployeeClientPersonalInput
    ) {
        updateEmployeeWithPersonal(id: $id, input: $input, clientPersonal: $clientPersonal) {
            id
            workEmail
            workPhone
            mobileNumber
            status
            hoursBasis
            minHoursPerWeek
            maxHoursPerWeek
            client {
                id
                fName
                lName
                email
                phoneNumber
            }
        }
    }
`;

export const UPDATE_EMPLOYEE_AWARD = gql`
    mutation UpdateEmployee($id: ID!, $input: UpdateEmployeeInput!) {
        updateEmployee(id: $id, input: $input) {
            id
            workEmail
            workPhone
            mobileNumber
            roleIds
            awardClassificationId
            awardClassification {
                id
                code
                name
                baseHourlyRate
                casualHourlyRate
            }
            status
        }
    }
`;

export const DELETE_EMPLOYEE = gql`
    mutation DeleteEmployee($id: ID!) {
        deleteEmployee(id: $id)
    }
`;

// ==========================================
// DUPLICATE EMPLOYEE QUERIES & MUTATIONS
// ==========================================

export const FIND_DUPLICATE_EMPLOYEES = gql`
    query FindDuplicateEmployees {
        findDuplicateEmployees {
            clientId
            count
            client {
                id
                fName
                lName
                email
                phoneNumber
            }
            employees {
                id
                employeeNumber
                position
                departmentIds
                departments {
                    id
                    name
                }
                status
                createdAt
                company {
                    id
                    name
                }
            }
        }
    }
`;

export const DELETE_DUPLICATE_EMPLOYEES = gql`
    mutation DeleteDuplicateEmployees($keepId: ID!, $deleteIds: [ID!]!) {
        deleteDuplicateEmployees(keepId: $keepId, deleteIds: $deleteIds)
    }
`;

export const FIND_POTENTIAL_DUPLICATES = gql`
    query FindPotentialDuplicates {
        findPotentialDuplicates {
            groupId
            reason
            matchValue
            count
            clients {
                client {
                    id
                    fName
                    lName
                    email
                    phoneNumber
                    createdAt
                }
                employee {
                    id
                    employeeNumber
                    position
                    status
                    departments {
                        id
                        name
                    }
                    company {
                        id
                        name
                    }
                }
            }
        }
    }
`;

export const MERGE_CLIENTS = gql`
    mutation MergeClients($keepClientId: ID!, $mergeClientIds: [ID!]!) {
        mergeClients(keepClientId: $keepClientId, mergeClientIds: $mergeClientIds) {
            id
            fName
            lName
            email
        }
    }
`;

// ==========================================
// CLIENT QUERIES & MUTATIONS
// ==========================================

export const GET_CLIENTS = gql`
    query GetClients {
        clients {
            id
            fName
            lName
            email
            phoneNumber
        }
    }
`;

export const GET_AVAILABLE_CLIENTS_FOR_EMPLOYEE = gql`
    query GetAvailableClientsForEmployee($excludeEmployeeId: ID) {
        availableClientsForEmployee(excludeEmployeeId: $excludeEmployeeId) {
            id
            fName
            lName
            email
            phoneNumber
        }
    }
`;

export const CREATE_CLIENT = gql`
    mutation CreateClient($input: ClientInput!) {
        createClient(input: $input) {
            id
            fName
            lName
            email
            phoneNumber
        }
    }
`;

export const UPDATE_CLIENT = gql`
    mutation UpdateClient($id: ID!, $input: UpdateClientInput!) {
        updateClient(id: $id, input: $input) {
            id
            fName
            lName
            email
            phoneNumber
        }
    }
`;

// ==========================================
// COMPANY QUERIES
// ==========================================

export const GET_COMPANIES = gql`
    query GetCompanies {
        companies {
            id
            name
        }
    }
`;

// ==========================================
// DEPARTMENT QUERIES & MUTATIONS
// ==========================================

export const GET_DEPARTMENTS = gql`
    query GetDepartments($includeInactive: Boolean) {
        departments(includeInactive: $includeInactive) {
            id
            code
            name
            description
            displayOrder
            isActive
            color
            countsTowardCareMinutes
            shiftUsageCount
            employeeUsageCount
            employeeUsageNames
        }
    }
`;

export const GET_DEPARTMENTS_SIMPLE = gql`
    query GetDepartments {
        departments {
            id
            code
            name
            displayOrder
        }
    }
`;

export const CREATE_DEPARTMENT = gql`
    mutation CreateDepartment($input: DepartmentInput!) {
        createDepartment(input: $input) {
            id
            code
            name
            description
            displayOrder
            isActive
            color
            countsTowardCareMinutes
        }
    }
`;

export const UPDATE_DEPARTMENT = gql`
    mutation UpdateDepartment($id: ID!, $input: UpdateDepartmentInput!) {
        updateDepartment(id: $id, input: $input) {
            id
            code
            name
            description
            displayOrder
            isActive
            color
            countsTowardCareMinutes
        }
    }
`;

export const DELETE_DEPARTMENT = gql`
    mutation DeleteDepartment($id: ID!, $hardDelete: Boolean) {
        deleteDepartment(id: $id, hardDelete: $hardDelete)
    }
`;

// ==========================================
// TEAM QUERIES & MUTATIONS
// ==========================================

export const GET_TEAMS = gql`
    query GetTeams($includeInactive: Boolean) {
        teams(includeInactive: $includeInactive) {
            id
            code
            name
            description
            displayOrder
            isActive
            color
            shiftUsageCount
        }
    }
`;

export const GET_TEAMS_SIMPLE = gql`
    query GetTeamsSimple {
        teams {
            id
            code
            name
            color
            displayOrder
        }
    }
`;

export const CREATE_TEAM = gql`
    mutation CreateTeam($input: TeamInput!) {
        createTeam(input: $input) {
            id
            code
            name
            description
            displayOrder
            isActive
            color
        }
    }
`;

export const UPDATE_TEAM = gql`
    mutation UpdateTeam($id: ID!, $input: UpdateTeamInput!) {
        updateTeam(id: $id, input: $input) {
            id
            code
            name
            description
            displayOrder
            isActive
            color
        }
    }
`;

export const DELETE_TEAM = gql`
    mutation DeleteTeam($id: ID!, $hardDelete: Boolean) {
        deleteTeam(id: $id, hardDelete: $hardDelete)
    }
`;

// ==========================================
// LOCATION QUERIES (read-only; locations module owns CRUD)
// ==========================================

export const GET_LOCATIONS_SIMPLE = gql`
    query GetLocationsSimple {
        locations {
            id
            name
            code
            type
            isActive
        }
    }
`;

// ==========================================
// EMPLOYEE ROLE QUERIES & MUTATIONS
// ==========================================

export const GET_EMPLOYEE_ROLES = gql`
    query GetEmployeeRoles($includeInactive: Boolean) {
        employeeRoles(includeInactive: $includeInactive) {
            id
            name
            shortCode
            description
            displayOrder
            isActive
            color
            countsTowardCareMinutes
            shiftUsageCount
            employeeUsageCount
            employeeUsageNames
        }
    }
`;

export const GET_EMPLOYEE_ROLES_SIMPLE = gql`
    query GetEmployeeRoles {
        employeeRoles {
            id
            name
            displayOrder
        }
    }
`;

export const CREATE_EMPLOYEE_ROLE = gql`
    mutation CreateEmployeeRole($input: EmployeeRoleInput!) {
        createEmployeeRole(input: $input) {
            id
            name
            description
            displayOrder
            isActive
            color
            countsTowardCareMinutes
        }
    }
`;

export const UPDATE_EMPLOYEE_ROLE = gql`
    mutation UpdateEmployeeRole($id: ID!, $input: UpdateEmployeeRoleInput!) {
        updateEmployeeRole(id: $id, input: $input) {
            id
            name
            description
            displayOrder
            isActive
            color
            countsTowardCareMinutes
        }
    }
`;

export const DELETE_EMPLOYEE_ROLE = gql`
    mutation DeleteEmployeeRole($id: ID!, $hardDelete: Boolean) {
        deleteEmployeeRole(id: $id, hardDelete: $hardDelete)
    }
`;

// ==========================================
// EMPLOYEE POSITION QUERIES & MUTATIONS
// ==========================================

export const GET_EMPLOYEE_POSITIONS = gql`
    query GetEmployeePositions($includeInactive: Boolean) {
        employeePositions(includeInactive: $includeInactive) {
            id
            name
            description
            displayOrder
            isActive
            color
            employeeUsageCount
            employeeUsageNames
        }
    }
`;

export const GET_EMPLOYEE_POSITIONS_SIMPLE = gql`
    query GetEmployeePositionsSimple {
        employeePositions {
            id
            name
            displayOrder
            color
        }
    }
`;

export const CREATE_EMPLOYEE_POSITION = gql`
    mutation CreateEmployeePosition($input: EmployeePositionInput!) {
        createEmployeePosition(input: $input) {
            id
            name
            description
            displayOrder
            isActive
            color
        }
    }
`;

export const UPDATE_EMPLOYEE_POSITION = gql`
    mutation UpdateEmployeePosition($id: ID!, $input: UpdateEmployeePositionInput!) {
        updateEmployeePosition(id: $id, input: $input) {
            id
            name
            description
            displayOrder
            isActive
            color
        }
    }
`;

export const DELETE_EMPLOYEE_POSITION = gql`
    mutation DeleteEmployeePosition($id: ID!, $hardDelete: Boolean) {
        deleteEmployeePosition(id: $id, hardDelete: $hardDelete)
    }
`;

// ==========================================
// EMPLOYEE STATUS OPTION QUERIES & MUTATIONS
// (tenant-editable status list; baseline rows are locked server-side)
// ==========================================

export const GET_EMPLOYEE_STATUS_OPTIONS = gql`
    query GetEmployeeStatusOptions($includeInactive: Boolean) {
        employeeStatusOptions(includeInactive: $includeInactive) {
            id
            name
            description
            displayOrder
            isActive
            color
            isBaseline
            employeeUsageCount
            employeeUsageNames
        }
    }
`;

export const GET_EMPLOYEE_STATUS_OPTIONS_SIMPLE = gql`
    query GetEmployeeStatusOptionsSimple {
        employeeStatusOptions {
            id
            name
            displayOrder
            color
            isBaseline
        }
    }
`;

export const CREATE_EMPLOYEE_STATUS_OPTION = gql`
    mutation CreateEmployeeStatusOption($input: EmployeeStatusOptionInput!) {
        createEmployeeStatusOption(input: $input) {
            id
            name
            description
            displayOrder
            isActive
            color
            isBaseline
        }
    }
`;

export const UPDATE_EMPLOYEE_STATUS_OPTION = gql`
    mutation UpdateEmployeeStatusOption($id: ID!, $input: UpdateEmployeeStatusOptionInput!) {
        updateEmployeeStatusOption(id: $id, input: $input) {
            id
            name
            description
            displayOrder
            isActive
            color
            isBaseline
        }
    }
`;

export const DELETE_EMPLOYEE_STATUS_OPTION = gql`
    mutation DeleteEmployeeStatusOption($id: ID!, $hardDelete: Boolean) {
        deleteEmployeeStatusOption(id: $id, hardDelete: $hardDelete)
    }
`;

// ==========================================
// AWARD CLASSIFICATION QUERIES & MUTATIONS
// ==========================================

export const GET_AWARD_CLASSIFICATIONS = gql`
    query GetAwardClassifications($includeInactive: Boolean, $awardName: String) {
        awardClassifications(includeInactive: $includeInactive, awardName: $awardName) {
            id
            code
            name
            awardName
            description
            payLevel
            baseHourlyRate
            casualHourlyRate
            displayOrder
            isActive
            color
        }
    }
`;

export const GET_AWARD_CLASSIFICATIONS_SIMPLE = gql`
    query GetAwardClassifications {
        awardClassifications {
            id
            code
            name
            awardName
            baseHourlyRate
            casualHourlyRate
            displayOrder
        }
    }
`;

export const GET_AWARD_NAMES = gql`
    query GetAwardNames {
        awardNames
    }
`;

export const CREATE_AWARD_CLASSIFICATION = gql`
    mutation CreateAwardClassification($input: AwardClassificationInput!) {
        createAwardClassification(input: $input) {
            id
            code
            name
            awardName
            description
            payLevel
            baseHourlyRate
            casualHourlyRate
            displayOrder
            isActive
            color
        }
    }
`;

export const UPDATE_AWARD_CLASSIFICATION = gql`
    mutation UpdateAwardClassification($id: ID!, $input: UpdateAwardClassificationInput!) {
        updateAwardClassification(id: $id, input: $input) {
            id
            code
            name
            awardName
            description
            payLevel
            baseHourlyRate
            casualHourlyRate
            displayOrder
            isActive
            color
        }
    }
`;

export const DELETE_AWARD_CLASSIFICATION = gql`
    mutation DeleteAwardClassification($id: ID!, $hardDelete: Boolean) {
        deleteAwardClassification(id: $id, hardDelete: $hardDelete)
    }
`;

// ==========================================
// EMPLOYEE ACTIVITY QUERIES & MUTATIONS
// ==========================================

export const GET_EMPLOYEE_ACTIVITIES = gql`
    query GetEmployeeActivities($employeeId: ID!, $filters: EmployeeActivityFiltersInput, $limit: Float, $offset: Float) {
        employeeActivities(employeeId: $employeeId, filters: $filters, limit: $limit, offset: $offset) {
            id
            title
            details
            activityType
            tags
            attachments {
                url
                fileName
                mimeType
                fileSize
                uploadedAt
            }
            createdByName
            createdById
            activityDate
            isPrivate
            createdAt
            updatedAt
        }
    }
`;

export const GET_EMPLOYEE_ACTIVITY_TYPES = gql`
    query GetEmployeeActivityTypes {
        employeeActivityTypes
    }
`;

export const CREATE_EMPLOYEE_ACTIVITY = gql`
    mutation CreateEmployeeActivity($employeeId: ID!, $input: EmployeeActivityInput!) {
        createEmployeeActivity(employeeId: $employeeId, input: $input) {
            id
            title
            details
            activityType
            tags
            attachments {
                url
                fileName
                mimeType
                fileSize
                uploadedAt
            }
            createdByName
            activityDate
            isPrivate
            createdAt
        }
    }
`;

export const UPDATE_EMPLOYEE_ACTIVITY = gql`
    mutation UpdateEmployeeActivity($id: ID!, $input: UpdateEmployeeActivityInput!) {
        updateEmployeeActivity(id: $id, input: $input) {
            id
            title
            details
            activityType
            tags
            attachments {
                url
                fileName
                mimeType
                fileSize
                uploadedAt
            }
            createdByName
            activityDate
            isPrivate
            updatedAt
        }
    }
`;

export const DELETE_EMPLOYEE_ACTIVITY = gql`
    mutation DeleteEmployeeActivity($id: ID!) {
        deleteEmployeeActivity(id: $id)
    }
`;

// ==========================================
// EMPLOYEE ACTIVITY TAG QUERIES & MUTATIONS
// ==========================================

export const GET_EMPLOYEE_ACTIVITY_TAGS = gql`
    query GetEmployeeActivityTags {
        employeeActivityTags {
            id
            name
            color
            usageCount
        }
    }
`;

export const GET_ACTIVITY_TAGS_COLORS = gql`
    query GetActivityTagsForColors {
        employeeActivityTags {
            id
            name
            color
        }
    }
`;

export const GET_ACTIVITY_TYPES_FILTER = gql`
    query GetActivityTypesForFilter {
        employeeActivityTypes
    }
`;

export const GET_TAG_COLORS = gql`
    query GetTagColors {
        suggestEmployeeActivityTagColors
    }
`;

export const UPSERT_EMPLOYEE_ACTIVITY_TAG = gql`
    mutation UpsertEmployeeActivityTag($input: EmployeeActivityTagInput!) {
        upsertEmployeeActivityTag(input: $input) {
            id
            name
            color
            usageCount
        }
    }
`;

// ==========================================
// LMS / TRAINING QUERIES & MUTATIONS
// ==========================================

export const GET_ENROLLMENTS_BY_USER_ID = gql`
    query GetEnrollmentsByUserId($userId: String!) {
        lmGetEnrollmentsByUserId(userId: $userId) {
            id
            courseId
            userId
            progressPercentage
            enrolledAt
            completedAt
            lastAccessedAt
            certificateGenerated
            certificateId
            completedLessonIds
        }
    }
`;

export const GET_LM_COURSES_SIMPLE = gql`
    query GetLmCourses {
        lmGetCourses {
            id
            title
            description
            isPublished
            enrollmentCount
        }
    }
`;

export const ADMIN_ENROLL_USER = gql`
    mutation AdminEnrollUser($userId: String!, $courseId: String!) {
        lmAdminEnrollUser(userId: $userId, courseId: $courseId) {
            id
            courseId
            progressPercentage
            enrolledAt
        }
    }
`;

// ==========================================
// EMPLOYEE TRAINING ASSIGNMENTS (Hybrid Module)
// ==========================================

export const GET_EMPLOYEE_TRAINING_ASSIGNMENTS = gql`
    query GetEmployeeTrainingAssignments($employeeId: ID!) {
        employeeTrainingAssignments(employeeId: $employeeId) {
            id
            employeeId
            courseId
            courseName
            enrollmentId
            assignedByName
            assignedAt
            trainingType
            priority
            dueDate
            expiresAt
            categoryId
            category {
                id
                name
                color
            }
            status
            completedAt
            progressPercentage
            isOverdue
            notes
        }
    }
`;

export const GET_EMPLOYEE_TRAINING_ASSIGNMENT = gql`
    query GetEmployeeTrainingAssignment($id: ID!) {
        employeeTrainingAssignment(id: $id) {
            id
            employeeId
            courseId
            courseName
            enrollmentId
            assignedByName
            assignedAt
            trainingType
            priority
            dueDate
            expiresAt
            categoryId
            category {
                id
                name
                color
            }
            status
            completedAt
            progressPercentage
            isOverdue
            notes
        }
    }
`;

export const GET_EMPLOYEE_TRAINING_CATEGORIES = gql`
    query GetEmployeeTrainingCategories($includeInactive: Boolean) {
        employeeTrainingCategories(includeInactive: $includeInactive) {
            id
            code
            name
            color
            renewalPeriodMonths
            isRequired
            displayOrder
            isActive
        }
    }
`;

export const GET_EMPLOYEE_TRAINING_DASHBOARD = gql`
    query GetEmployeeTrainingDashboard {
        employeeTrainingDashboard {
            totalAssignments
            overdueCount
            dueThisWeek
            expiredCount
            completedCount
            inProgressCount
        }
    }
`;

export const ASSIGN_EMPLOYEE_TRAINING = gql`
    mutation AssignEmployeeTraining($input: EmployeeTrainingAssignmentInput!) {
        assignEmployeeTraining(input: $input) {
            id
            courseId
            courseName
            status
            assignedAt
            trainingType
            priority
            dueDate
        }
    }
`;

export const UPDATE_EMPLOYEE_TRAINING_ASSIGNMENT = gql`
    mutation UpdateEmployeeTrainingAssignment($id: ID!, $input: UpdateEmployeeTrainingAssignmentInput!) {
        updateEmployeeTrainingAssignment(id: $id, input: $input) {
            id
            status
            dueDate
            priority
            trainingType
            notes
        }
    }
`;

export const DELETE_EMPLOYEE_TRAINING_ASSIGNMENT = gql`
    mutation DeleteEmployeeTrainingAssignment($id: ID!) {
        deleteEmployeeTrainingAssignment(id: $id)
    }
`;

export const CREATE_EMPLOYEE_TRAINING_CATEGORY = gql`
    mutation CreateEmployeeTrainingCategory($input: EmployeeTrainingCategoryInput!) {
        createEmployeeTrainingCategory(input: $input) {
            id
            code
            name
            color
            renewalPeriodMonths
            isRequired
        }
    }
`;

export const UPDATE_EMPLOYEE_TRAINING_CATEGORY = gql`
    mutation UpdateEmployeeTrainingCategory($id: ID!, $input: UpdateEmployeeTrainingCategoryInput!) {
        updateEmployeeTrainingCategory(id: $id, input: $input) {
            id
            code
            name
            color
            renewalPeriodMonths
            isRequired
            isActive
        }
    }
`;

export const DELETE_EMPLOYEE_TRAINING_CATEGORY = gql`
    mutation DeleteEmployeeTrainingCategory($id: ID!) {
        deleteEmployeeTrainingCategory(id: $id)
    }
`;

// ==========================================
// EMPLOYEE ONBOARDING INVITES
// ==========================================

export const CREATE_EMPLOYEE_INVITE = gql`
    mutation CreateEmployeeInvite($input: CreateEmployeeInviteInput!) {
        createEmployeeInvite(input: $input) {
            id
            slug
            email
            status
            expiresAt
            emailSentAt
        }
    }
`;

export const GET_EMPLOYEE_INVITES = gql`
    query EmployeeInvites {
        employeeInvites {
            id
            slug
            email
            companyId
            position
            status
            createdByName
            createdAt
            completedAt
            expiresAt
            emailSentAt
            employeeId
            clientId
            requiredFormTypes
            reminderLastSentAt
            progress {
                sections {
                    key
                    label
                    complete
                    completedAt
                }
                completedCount
                totalCount
                percent
            }
        }
    }
`;

export const SEND_EMPLOYEE_INVITE_REMINDER = gql`
    mutation SendEmployeeInviteReminder($id: ID!, $customMessage: String) {
        sendEmployeeInviteReminder(id: $id, customMessage: $customMessage) {
            id
            reminderLastSentAt
        }
    }
`;

export const PUBLIC_EMPLOYEE_INVITE = gql`
    query PublicEmployeeInvite($slug: String!, $tenantId: String!) {
        publicEmployeeInvite(slug: $slug, tenantId: $tenantId) {
            email
            companyName
            position
            departmentName
            status
        }
    }
`;

export const DELETE_EMPLOYEE_INVITE = gql`
    mutation DeleteEmployeeInvite($id: ID!) {
        deleteEmployeeInvite(id: $id)
    }
`;

export const SUBMIT_EMPLOYEE_ONBOARDING = gql`
    mutation SubmitEmployeeOnboarding($input: SubmitEmployeeOnboardingInput!) {
        submitEmployeeOnboarding(input: $input) {
            success
            username
            temporaryPassword
            email
            companyName
        }
    }
`;

export const GENERATE_EMPLOYEE_CREDENTIALS = gql`
    mutation GenerateEmployeeCredentials($employeeId: ID!, $sendSms: Boolean) {
        generateEmployeeCredentials(employeeId: $employeeId, sendSms: $sendSms) {
            success
            username
            temporaryPassword
            email
            companyName
        }
    }
`;

export const ADMIN_RESET_PASSWORD = gql`
    mutation AdminResetPassword($input: AdminResetPasswordInput!) {
        adminResetPassword(input: $input)
    }
`;

export const GET_LOGIN_ACTIVITIES = gql`
    query LoginActivities($clientId: ID!, $limit: Int, $offset: Int) {
        loginActivities(clientId: $clientId, limit: $limit, offset: $offset) {
            id
            action
            method
            ipAddress
            userAgent
            performedByName
            details
            timestamp
        }
    }
`;

export const SEND_EMPLOYEE_MESSAGE = gql`
    mutation SendEmployeeMessage($employeeId: ID!, $message: String!, $channel: String!, $subject: String) {
        sendEmployeeMessage(employeeId: $employeeId, message: $message, channel: $channel, subject: $subject)
    }
`;

// ==========================================
// SELF-SERVICE (MY DETAILS)
// ==========================================

export const GET_MY_EMPLOYEE_PROFILE = gql`
    query MyEmployeeProfile {
        myEmployeeProfile {
            id
            clientId
            employeeNumber
            client {
                id
                fName
                lName
                email
                phoneNumber
            }
            preferredName
            mobileNumber
            workEmail
            workPhone
            roleIds
            roles {
                id
                name
                color
            }
            departmentIds
            departments {
                id
                name
                color
            }
            locationIds
            locations {
                id
                name
            }
            positionIds
            positions {
                id
                name
                color
            }
            position
            status
            startDate
            endDate
            hourlyRate
            annualSalary
            hoursBasis
            minHoursPerWeek
            maxHoursPerWeek
            address {
                street
                city
                state
                postcode
                country
            }
            dateOfBirth
            gender
            taxFileNumber
            superannuationFund
            bankAccountName
            bankAccountNumber
            bankBSB
            emergencyContact {
                name
                relationship
                phoneNumber
                email
            }
            profilePhoto
            qualifications
            certifications
            contractType
            isShiftWorker
            workingRights
            visaCountry
            taxResidencyStatus
            claimsTaxFreeThreshold
            hasStudyTrainingLoan
            superChoiceType
            superFundABN
            superFundUSI
            superMemberNumber
            superNameOnAccount
            smsfAbn
            smsfEsa
            smsfBankAccountName
            smsfBankBSB
            smsfBankAccountNumber
            company {
                id
                name
            }
            createdAt
            updatedAt
        }
    }
`;

export const UPDATE_MY_EMPLOYEE_PROFILE = gql`
    mutation UpdateMyEmployeeProfile(
        $employeeInput: UpdateMyEmployeeProfileInput!
        $clientInput: UpdateMyClientProfileInput
    ) {
        updateMyEmployeeProfile(employeeInput: $employeeInput, clientInput: $clientInput) {
            id
            client {
                id
                fName
                lName
                email
                phoneNumber
            }
            preferredName
            address {
                street
                city
                state
                postcode
                country
            }
            dateOfBirth
            gender
            emergencyContact {
                name
                relationship
                phoneNumber
                email
            }
            bankAccountName
            bankBSB
            bankAccountNumber
            taxFileNumber
            superannuationFund
            workingRights
            visaCountry
            taxResidencyStatus
            claimsTaxFreeThreshold
            hasStudyTrainingLoan
            superChoiceType
            superFundABN
            superFundUSI
            superMemberNumber
            superNameOnAccount
            smsfAbn
            smsfEsa
            smsfBankAccountName
            smsfBankBSB
            smsfBankAccountNumber
            qualifications
            certifications
            profilePhoto
        }
    }
`;

// ==========================================
// CLIENT PERMISSION MUTATIONS (for employee detail page)
// ==========================================

export const ADD_CLIENT_PERMISSION = gql`
    mutation AddClientPermission($clientId: ID!, $permission: ClientPermission!) {
        addClientPermission(clientId: $clientId, permission: $permission) {
            id
            permissions
        }
    }
`;

export const REMOVE_CLIENT_PERMISSION = gql`
    mutation RemoveClientPermission($clientId: ID!, $permission: ClientPermission!) {
        removeClientPermission(clientId: $clientId, permission: $permission) {
            id
            permissions
        }
    }
`;

// ==========================================
// TENANT CURATED PERMISSIONS
// ==========================================

export const GET_TENANT_CURATED_PERMISSIONS = gql`
    query GetTenantCuratedPermissions {
        currentTenant {
            id
            curatedEmployeePermissions
        }
    }
`;

export const CREATE_EMPLOYEE_WITH_CREDENTIALS = gql`
    mutation CreateEmployeeWithCredentials($input: CreateEmployeeWithCredentialsInput!) {
        createEmployeeWithCredentials(input: $input) {
            success
            username
            temporaryPassword
            email
            companyName
        }
    }
`;

// ==========================================
// SIGNED ONBOARDING FORMS (TFN declaration / super choice — 2026-08-05)
// ==========================================

export const GET_MY_EMPLOYEE_FORMS = gql`
    query MyEmployeeForms {
        myEmployeeForms {
            formType
            title
            formVersion
            mandatory
            signed
            signedAt
            signedFormId
            signedPdfUrl
            prefillJson
            declarationText
            missingFields
        }
    }
`;

export const SIGN_MY_EMPLOYEE_FORM = gql`
    mutation SignMyEmployeeForm($input: SignMyEmployeeFormInput!) {
        signMyEmployeeForm(input: $input) {
            id
            formType
            formVersion
            signedAt
            signedPdfUrl
            formPayloadHash
        }
    }
`;

export const GET_EMPLOYEE_SIGNED_FORMS = gql`
    query EmployeeSignedForms($employeeId: ID!) {
        employeeSignedForms(employeeId: $employeeId) {
            id
            formType
            formVersion
            signerName
            signedAt
            signedPdfUrl
            formPayloadHash
            ipAddress
        }
    }
`;

export const GET_EMPLOYEE_FORM_SETTINGS = gql`
    query EmployeeFormSettings {
        employeeFormSettings {
            id
            formType
            enabled
            mandatory
            employerBusinessName
            employerAbn
            defaultFundName
            defaultFundAbn
            defaultFundUsi
        }
    }
`;

export const UPDATE_EMPLOYEE_FORM_SETTING = gql`
    mutation UpdateEmployeeFormSetting($input: UpdateEmployeeFormSettingInput!) {
        updateEmployeeFormSetting(input: $input) {
            id
            formType
            enabled
            mandatory
            employerBusinessName
            employerAbn
            defaultFundName
            defaultFundAbn
            defaultFundUsi
        }
    }
`;

// ==========================================
// EMPLOYEE INCIDENT QUERIES & MUTATIONS
// ==========================================

export const SUBMIT_MY_EMPLOYEE_INCIDENT = gql`
    mutation SubmitMyEmployeeIncident($input: SubmitEmployeeIncidentInput!) {
        submitMyEmployeeIncident(input: $input) {
            id
            incidentNumber
            incidentType
            status
            incidentDate
            description
            employeeName
            createdAt
        }
    }
`;

export const GET_MY_EMPLOYEE_INCIDENTS = gql`
    query GetMyEmployeeIncidents {
        getMyEmployeeIncidents {
            id
            incidentNumber
            incidentType
            status
            incidentDate
            incidentTime
            location
            description
            bodyPartAffected
            howInjuryOccurred
            immediateActionTaken
            medicalAttentionNeeded
            gpReferralNeeded
            harassmentSubType
            personsInvolved
            witnesses
            natureOfMisconduct
            attachments {
                url
                fileName
                mimeType
                fileSize
                uploadedAt
            }
            reviewNotes
            reviewedByName
            reviewedAt
            resolvedAt
            closedAt
            createdAt
        }
    }
`;

export const GET_EMPLOYEE_INCIDENTS = gql`
    query EmployeeIncidents($filters: EmployeeIncidentFilterInput, $limit: Int, $offset: Int) {
        employeeIncidents(filters: $filters, limit: $limit, offset: $offset) {
            incidents {
                id
                incidentNumber
                employeeId
                employeeName
                incidentType
                status
                incidentDate
                incidentTime
                location
                description
                bodyPartAffected
                howInjuryOccurred
                immediateActionTaken
                medicalAttentionNeeded
                gpReferralNeeded
                harassmentSubType
                personsInvolved
                witnesses
                natureOfMisconduct
                attachments {
                    url
                    fileName
                    mimeType
                    fileSize
                    uploadedAt
                }
                reviewedById
                reviewedByName
                reviewedAt
                reviewNotes
                resolvedAt
                closedAt
                createdAt
            }
            total
        }
    }
`;

export const GET_EMPLOYEE_INCIDENT = gql`
    query EmployeeIncident($id: ID!) {
        employeeIncident(id: $id) {
            id
            incidentNumber
            employeeId
            employeeName
            incidentType
            status
            incidentDate
            incidentTime
            location
            description
            bodyPartAffected
            howInjuryOccurred
            immediateActionTaken
            medicalAttentionNeeded
            gpReferralNeeded
            harassmentSubType
            personsInvolved
            witnesses
            natureOfMisconduct
            attachments {
                url
                fileName
                mimeType
                fileSize
                uploadedAt
            }
            activityLogEntryId
            reviewedById
            reviewedByName
            reviewedAt
            reviewNotes
            resolvedAt
            closedAt
            createdAt
            updatedAt
        }
    }
`;

export const UPDATE_EMPLOYEE_INCIDENT = gql`
    mutation UpdateEmployeeIncident($id: ID!, $input: UpdateEmployeeIncidentInput!) {
        updateEmployeeIncident(id: $id, input: $input) {
            id
            incidentNumber
            status
            reviewNotes
            reviewedByName
            reviewedAt
            resolvedAt
            closedAt
            updatedAt
        }
    }
`;

// ==========================================
// EMPLOYEE INCIDENT NOTIFICATION RULES
// ==========================================

export const GET_EMPLOYEE_INCIDENT_NOTIFICATION_RULES = gql`
    query EmployeeIncidentNotificationRules {
        employeeIncidentNotificationRules {
            id
            name
            trigger
            recipientType
            recipientIds
            recipientRole
            channels
            isEnabled
            messageTemplate
            createdAt
        }
    }
`;

export const CREATE_EMPLOYEE_INCIDENT_NOTIFICATION_RULE = gql`
    mutation CreateEmployeeIncidentNotificationRule($input: CreateEmployeeIncidentNotificationRuleInput!) {
        createEmployeeIncidentNotificationRule(input: $input) {
            id
            name
            trigger
            recipientType
            recipientIds
            channels
            isEnabled
        }
    }
`;

export const UPDATE_EMPLOYEE_INCIDENT_NOTIFICATION_RULE = gql`
    mutation UpdateEmployeeIncidentNotificationRule($input: UpdateEmployeeIncidentNotificationRuleInput!) {
        updateEmployeeIncidentNotificationRule(input: $input) {
            id
            name
            trigger
            recipientType
            recipientIds
            channels
            isEnabled
            messageTemplate
        }
    }
`;

export const DELETE_EMPLOYEE_INCIDENT_NOTIFICATION_RULE = gql`
    mutation DeleteEmployeeIncidentNotificationRule($id: ID!) {
        deleteEmployeeIncidentNotificationRule(id: $id)
    }
`;

export const TOGGLE_EMPLOYEE_INCIDENT_NOTIFICATION_RULE = gql`
    mutation ToggleEmployeeIncidentNotificationRule($id: ID!) {
        toggleEmployeeIncidentNotificationRule(id: $id) {
            id
            isEnabled
        }
    }
`;

// ==========================================
// EMPLOYEE NOTIFICATION RULES (comprehensive — 26 triggers)
// ==========================================

export const GET_EMPLOYEE_NOTIFICATION_RULES = gql`
    query EmployeeNotificationRules {
        employeeNotificationRules {
            id
            name
            trigger
            recipientType
            recipientIds
            recipientRole
            recipientDepartmentId
            channels
            isEnabled
            messageTemplate
            filterDepartmentId
            chatSessionGapMinutes
            chatRecipientCooldownMinutes
            createdAt
        }
    }
`;

export const CREATE_EMPLOYEE_NOTIFICATION_RULE = gql`
    mutation CreateEmployeeNotificationRule($input: CreateEmployeeNotificationRuleInput!) {
        createEmployeeNotificationRule(input: $input) {
            id
            name
            trigger
            recipientType
            recipientIds
            recipientDepartmentId
            channels
            isEnabled
            filterDepartmentId
            chatSessionGapMinutes
            chatRecipientCooldownMinutes
        }
    }
`;

export const UPDATE_EMPLOYEE_NOTIFICATION_RULE = gql`
    mutation UpdateEmployeeNotificationRule($input: UpdateEmployeeNotificationRuleInput!) {
        updateEmployeeNotificationRule(input: $input) {
            id
            name
            trigger
            recipientType
            recipientIds
            recipientDepartmentId
            channels
            isEnabled
            messageTemplate
            filterDepartmentId
            chatSessionGapMinutes
            chatRecipientCooldownMinutes
        }
    }
`;

export const DELETE_EMPLOYEE_NOTIFICATION_RULE = gql`
    mutation DeleteEmployeeNotificationRule($id: ID!) {
        deleteEmployeeNotificationRule(id: $id)
    }
`;

export const TOGGLE_EMPLOYEE_NOTIFICATION_RULE = gql`
    mutation ToggleEmployeeNotificationRule($id: ID!) {
        toggleEmployeeNotificationRule(id: $id) {
            id
            isEnabled
        }
    }
`;

// ==========================================
// FILE UPLOAD — see ./hooks/useEmployeeUpload.ts
// ==========================================
// Profile-photo + activity-log uploads go through the module-local chunked
// upload hook (backend `employeeUpload.resolver.ts`, gated by
// employeesPermissions.upload). The old single-shot `uploadUnencryptedFile`
// mutation was deleted backend-side on 2026-06-30 (tenant-aware IPFS refactor).

// ==========================================
// EMAIL & CALENDAR PROVISIONING (Email & Calendar tab)
// ==========================================

// Tenant config the tab needs (locked email domain + Postmark inbound).
// Module-local copy per feedback_module_independence — do NOT cross-import the
// emails module's identical query.
export const GET_CURRENT_TENANT_EMAIL_CONFIG_FOR_EMPLOYEES = gql`
    query GetCurrentTenantEmailConfigForEmployees {
        currentTenant {
            id
            apiKeys {
                postmarkInboundAddress
                azureFunctionProvisionUrl
                azureFunctionKey
                microsoft365TenantId
                microsoft365ClientId
                microsoft365CertThumbprint
                microsoft365Organization
            }
            emailConfig {
                emailDomain
                fromEmail
            }
        }
    }
`;

export const PROVISION_EMPLOYEE_WORK_EMAIL = gql`
    mutation ProvisionEmployeeWorkEmail($employeeId: String!, $mailNickname: String!) {
        provisionEmployeeWorkEmail(employeeId: $employeeId, mailNickname: $mailNickname) {
            employee {
                id
                workEmail
            }
            emailAddress {
                id
                email
                name
                primaryCalendarId
                associatedClients
                postmarkEndpoint
                forwardingProvider
            }
            calendar {
                id
                name
                type
                linkedEmailAddressId
                responsibleOwnerId
            }
            m365SetupStatus {
                sharedMailboxLikely
                transportRuleLikely
                automationAttempted
                automationErrors
            }
        }
    }
`;

// ==========================================
// PASSKEY READINESS (manager dashboard)
// ==========================================
export const GET_TEAM_PASSKEY_READINESS = gql`
    query TeamPasskeyReadiness {
        teamPasskeyReadiness {
            employeeId
            employeeNumber
            name
            status
            department
            hasLogin
            hasPasskey
            passkeyCount
            lastPasskeyUsedAt
            lastLoginAt
            lastLoginMethod
        }
    }
`;

// ==========================================
// EMPLOYEE CHUNKED UPLOAD (module-local IPFS upload path)
// Moved here from hooks/useEmployeeUpload.ts 2026-07-18 — queries.ts is the
// only file in the module allowed to import gql (centralized queries pattern).
// ==========================================
export const EMPLOYEE_INITIATE_UPLOAD = gql`
  mutation EmployeeInitiateUpload($fileName: String!, $totalChunks: Float!, $fileSize: Float!) {
    employeeInitiateUpload(fileName: $fileName, totalChunks: $totalChunks, fileSize: $fileSize)
  }
`;

export const EMPLOYEE_UPLOAD_CHUNK = gql`
  mutation EmployeeUploadChunk($uploadId: String!, $chunkNumber: Float!, $chunk: Upload!) {
    employeeUploadChunk(uploadId: $uploadId, chunkNumber: $chunkNumber, chunk: $chunk)
  }
`;

export const EMPLOYEE_COMPLETE_UPLOAD = gql`
  mutation EmployeeCompleteUpload($uploadId: String!) {
    employeeCompleteUpload(uploadId: $uploadId) {
      ipfsHash
      url
      fileSize
      mimeType
      fileName
    }
  }
`;

export const EMPLOYEE_CANCEL_UPLOAD = gql`
  mutation EmployeeCancelUpload($uploadId: String!) {
    employeeCancelUpload(uploadId: $uploadId)
  }
`;

export const EMPLOYEE_GET_UPLOAD_PROGRESS = gql`
  mutation EmployeeGetUploadProgress($uploadId: String!) {
    employeeGetUploadProgress(uploadId: $uploadId)
  }
`;

// ==========================================
// EMPLOYEE QUICK NAVIGATION (prev/next + search on EmployeeDetails)
// ==========================================

export const GET_EMPLOYEES_FOR_NAVIGATION = gql`
    query GetEmployeesForNavigation($input: EmployeePaginationInput) {
        employeesPaginated(input: $input) {
            employees {
                id
                employeeNumber
                preferredName
                status
                client {
                    id
                    fName
                    lName
                }
            }
            total
        }
    }
`;

export const RENEW_EMPLOYEE_INVITE = gql`
    mutation RenewEmployeeInvite($id: ID!) {
        renewEmployeeInvite(id: $id) {
            id
            status
            expiresAt
            emailSentAt
        }
    }
`;
